import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import  LogoContainer from '../../../../../shared/components/LogoContainer'
import { createSaving } from '../../../../../../app/store/slices/savingSlice';
import { selectAccounts, fetchAccounts } from '../../../../../../app/store/slices/accountSlice';
import  AddAnotherList from '../../../../../shared/components/AddAnotherList';
import  Spinner  from '../../../../../shared/components/Spinner';

const SavingSetup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const accounts = useSelector(selectAccounts);

  const [savings, setSavings] = useState([]);
  const [currentSaving, setCurrentSaving] = useState({
    title: '',
    targetAmount: '',
    currentAmount: '',
    targetDate: '',
    linkedAccountId: location.state?.accountId || '',
    destinationAccountId: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedCanvasId, setSelectedCanvasId] = useState(
    JSON.parse(localStorage.getItem('onboardingCanvas'))?._id ||
    location.state?.canvasId
  );

  useEffect(() => {
    if (selectedCanvasId) {
      dispatch(fetchAccounts({ canvasId: selectedCanvasId , limit:1000}));
    }
  }, [dispatch, selectedCanvasId]);
  useEffect(() => {
    if (!selectedCanvasId && location.state?.canvasId) {
      setSelectedCanvasId(location.state.canvasId);
    }
  }, [location.state, selectedCanvasId]);

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentSaving({ ...currentSaving, [name]: value });
  };

  // Add another saving
  const handleAddSaving = async () => {
    if (
      currentSaving.title &&
      currentSaving.targetAmount &&
      currentSaving.linkedAccountId &&
      selectedCanvasId
    ) {
      try {
        await dispatch(createSaving({
          canvasId: selectedCanvasId,
          savingData: {
            title: currentSaving.title,
            targetAmount: Number(currentSaving.targetAmount),
            currentAmount: Number(currentSaving.currentAmount) || 0,
             targetDate: currentSaving.targetDate ? new Date(currentSaving.targetDate) : undefined,
            paymentAccountId: currentSaving.linkedAccountId,
            destinationAccountId: currentSaving.destinationAccountId
          }
        })).unwrap();
        setSavings([...savings, currentSaving]);
        setCurrentSaving({
          title: '',
          targetAmount: '',
          currentAmount: '',
          linkedAccountId: location.state?.accountId || ''
        });
      } catch (err) {
        console.error('Failed to create saving:', err);
      }
    }
  };

  // Continue button handler
  const handleContinue = async () => {
    setIsProcessing(true);
    try {
      if (
        currentSaving.title &&
        currentSaving.targetAmount &&
        currentSaving.linkedAccountId &&
        selectedCanvasId
      ) {
        await dispatch(createSaving({
          canvasId: selectedCanvasId,
          savingData: {
            title: currentSaving.title,
            targetAmount: Number(currentSaving.targetAmount),
            currentAmount: Number(currentSaving.currentAmount) || 0,
            targetDate: currentSaving.targetDate ? new Date(currentSaving.targetDate) : undefined,
            paymentAccountId: currentSaving.linkedAccountId,
            destinationAccountId: currentSaving.destinationAccountId
          }
        })).unwrap();
        setSavings(prev => [...prev, currentSaving]);
        setCurrentSaving({
          title: '',
          targetAmount: '',
          currentAmount: '',
          targetDate: '',
          linkedAccountId: location.state?.accountId || ''
        });
      }
      navigate('/debt', { state: { accountId: currentSaving.linkedAccountId, canvasId: selectedCanvasId } });
    } catch (err) {
      console.error('Failed to create saving:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <LogoContainer/>
      <div className='card-setup'>
        <h2 className='txt-page-heading'>Enter your Existing Savings</h2>
        <p className="p-tag-below-heading text-center">
          Enter your existing savings goals and progress
        </p>
        <div className="w-full flex flex-col items-center">
          <div className="space-y-4 w-full max-w-lg mx-auto">
            {/* Purpose */}
            <div className="flex items-center justify-center gap-3 w-full">
              <label htmlFor="savingTitle" className="sm:w-44 w-32 text-left font-medium text-gray-700 text-base">
                Purpose
              </label>
              <input
                type="text"
                id="savingTitle"
                name="title"
                value={currentSaving.title}
                onChange={handleInputChange}
                placeholder="eg wedding"
                className="input-field flex-1 w-full max-w-[400px]"
              />
            </div>
            {/* Target Amount */}
            <div className="flex items-center justify-center gap-3 w-full">
              <label htmlFor="targetAmount" className="sm:w-44 w-32 text-left font-medium text-gray-700 text-base">
                Target Amount
              </label>
              <input
                type="number"
                id="targetAmount"
                name="targetAmount"
                value={currentSaving.targetAmount}
                onChange={handleInputChange}
                placeholder="Target amount"
                className="input-field flex-1 w-full max-w-[400px]"
                min="0"
              />
            </div>
            {/* Amount Saved */}
            <div className="flex items-center justify-center gap-3 w-full">
              <label htmlFor="currentAmount" className="sm:w-44 w-32 text-left font-medium text-gray-700 text-base">
                Amount Saved
              </label>
              <input
                type="number"
                id="currentAmount"
                name="currentAmount"
                value={currentSaving.currentAmount}
                onChange={handleInputChange}
                placeholder="Amount saved"
                className="input-field flex-1 w-full max-w-[400px]"
                min="0"
              />
            </div>
            <div className="flex items-center justify-center gap-3 w-full">
              <label htmlFor="targetDate" className="sm:w-44 w-32 text-left font-medium text-gray-700 text-base">
                Target Date
              </label>
              <input
                type="date"
                id="targetDate"
                name="targetDate"
                value={currentSaving.targetDate}
                onChange={handleInputChange}
                className="input-field flex-1 w-full max-w-[400px]"
              />
            </div>
            {/* Linked Account */}
            <div className="flex items-center justify-center gap-3 w-full">
              <label htmlFor="linkedAccountId" className="sm:w-44 w-32 text-left font-medium text-gray-700 text-base">
                Linked Account
              </label>
              <select
                id="linkedAccountId"
                name="linkedAccountId"
                value={currentSaving.linkedAccountId}
                onChange={handleInputChange}
                className="input-field flex-1 w-full max-w-[400px] text-gray-500"
                required
              >
                <option value="">Select Account</option>
                {accounts.map(acc => (
                  <option key={acc._id} value={acc._id}>
                    {acc.name} ({acc.currency})
                  </option>
                ))}
              </select>
            </div>
            {/* Destination Account */}
            <div className="flex items-center justify-center gap-3 w-full">
              <label htmlFor="destinationAccountId" className="sm:w-44 w-32 text-left font-medium text-gray-700 text-base">
                Destination Account
              </label>
              <select
                id="destinationAccountId"
                name="destinationAccountId"
                value={currentSaving.destinationAccountId}
                onChange={handleInputChange}
                className="input-field flex-1 w-full max-w-[400px] text-gray-500"
                required
              >
                <option value="">Select Account</option>
                {accounts
                  .map(acc => (
                    <option key={acc._id} value={acc._id}>
                      {acc.name} ({acc.currency})
                    </option>
                  ))}
              </select>
            </div>
          </div>
          {/* Centered action buttons */}
          <div className="mt-8 flex flex-row justify-center gap-6 w-full max-w-lg">
            <button
              type="button"
              className="btn-back flex-1"
              onClick={() => {
                setCurrentSaving({
                  title: '',
                  targetAmount: '',
                  currentAmount: '',
                  linkedAccountId: location.state?.accountId || ''
                });
                navigate('/debt', { state: { accountId: currentSaving.linkedAccountId, canvasId: selectedCanvasId } });
              }}
            >
              Skip
            </button>
            <button
              type="button"
              className="btn-continue flex-1"
              onClick={handleAddSaving}
            >
              Add Another Saving
            </button>
          </div>
        </div>

        <AddAnotherList
          title="Your Savings"
          items={savings}
          renderItem={(saving) => (
            <div>
              <div className="font-medium">{saving.title}</div>
              <div className="text-sm text-gray-500">{saving.targetAmount}</div>
            </div>
          )}
        />
        <div className="flex flex-col md:flex-row justify-between gap-4 mt-8">
          <button
            className="btn-back"
            onClick={() => navigate(-1)}
          >
            Back
          </button>

          <button
            className="btn-continue"
            onClick={handleContinue}
          >
            {isProcessing ? (
              <Spinner text = "Processing..."/>
            ) : (
              'Save & Continue'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SavingSetup;